const API_URL = process.env.CLIENT_API_URL;
const WEBSHOT_API_URL = process.env.WEBSHOT_API_URL;
const BILLING_URL = `${API_URL}/billing`;

/* globals localStorage */
define([
  'react',
  'prop-types',
  'react-router',
  'history',
  'underscore',
  'axios',
  'moment',
  'URI',
  'es6!sumo/util/Constants',
  'es6!sumo/util/serverCalls',
], (React, PropTypes, ReactRouter, History, _, axios, moment, URI, Constants, serverCalls) => {
  const MEDIA_URL = '//media.sumome.com';

  const UNITS = {
    'yy': 'year',
    'MM': 'month',
    'dd': 'day',
    'hh': 'hour',
    'mm': 'minute',
    'ss': 'second',
  };

  var hasOwn = {}.hasOwnProperty;

  const containsPrize = text => (
    text.indexOf('{prize}') > -1 ||
    text.indexOf('{discount_code}') > -1 ||
    text.indexOf('{discount}') > -1
  );

  // Returns an object with all the query params listed.
  // A 'true' value means the query param exists, but doesn't have a value
  const getQueryVariables = () => {
    const vars = {};
    const query = window.location.search;
    const values = query.substr(1).split('&');
    values.forEach((queryStr) => {
      const parts = queryStr.split('=');
      if (parts.length === 1) {
        vars[parts[0]] = true;
      } else if (parts.length === 2) {
        vars[parts[0]] = decodeURIComponent(parts[1] || '');
      }
    });

    return vars;
  };

  const getQueryVariable = (variable) => {
    const query = window.location.search.substring(1);
    const vars = query.split('&');

    for (let i = 0; i < vars.length; i += 1) {
      const pair = vars[i].split('=');
      if (decodeURIComponent(pair[0]) === variable) {

        return decodeURIComponent(pair[1]);
      }
    }
    return null;
  };

  function copyToClipboard(text) {
    let textarea = document.createElement('TEXTAREA');
    textarea.value = text;
    document.body.append(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    textarea = null;
  }

  function classNames() {
    var classes = [];

    for (var i = 0; i < arguments.length; i++) {
      var arg = arguments[i];
      if (!arg) continue;

      var argType = typeof arg;

      if (argType === 'string' || argType === 'number') {
        classes.push(arg);
      } else if (Array.isArray(arg)) {
        classes.push(classNames.apply(null, arg));
      } else if (argType === 'object') {
        for (var key in arg) {
          if (hasOwn.call(arg, key) && arg[key]) {
            classes.push(key);
          }
        }
      }
    }

    return classes.join(' ');
  }

  const createReducer = (initialState, actionHandlers, opts = {}) => (s, action) => {
    const { beforeUpdate, afterUpdate } = opts;
    let state = s;

    if (state === undefined) state = initialState;

    const reduceFn = actionHandlers[action.type];
    if (!reduceFn) return state;

    if (beforeUpdate && typeof beforeUpdate === 'function') state = beforeUpdate(state, action);
    state = reduceFn(state, action);
    if (afterUpdate && typeof afterUpdate === 'function') state = afterUpdate(state, action);

    return state;
  };

  function getIn(obj, path) {
    var stack = path.split('.');

    while ( stack.length ) {
      obj = obj[stack.shift()];
    }

    return obj;
  }

  function updateIn(obj, path, value) {
    var current = obj;
    var stack = path.split('.');

    while ( stack.length > 1 ) {
      current = current[stack.shift()];
    }
    current[stack.shift()] = value;

    return obj;
  }

  function linkState(ctx, path) {
    return {
      value: getIn(ctx.state, path),
      requestChange: function setPartialState(value) {
        ctx.setState(updateIn(
          ctx.state,
          path,
          value
        ));
      }
    };
  }

  function numberWithCommas(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function _r() {
    return (Math.random().toString(16)+"000000000").substr(2,8).toLowerCase();
  }

  function generateId64() {
    return _r() + _r() + _r() + _r() + _r() + _r() + _r() + _r();
  }

  function sumoApi() {
    return function (DecoratedComponent) {
      const c = class extends React.Component {
        render() {
          return (
            <DecoratedComponent api={this.context.api} {...this.props} />
          );
        }
      };

      c.contextTypes = {
        api: PropTypes.object
      };

      return c;
    }
  }

  function isDimension(feature) {
    const re = /[height|width]$/;

    return re.test(feature);
  }

  function obj2mq(obj) {
    var mq = '';
    var features = Object.keys(obj);
    features.forEach(function (feature, index) {
      var value = obj[feature];
      feature = feature.replace(/([A-Z])/g, function($1){return "-"+$1.toLowerCase();});
      // Add px to dimension features
      if (isDimension(feature) && typeof value === 'number') {
        value = value + 'px';
      }
      if (value === true) {
        mq += feature;
      } else if (value === false) {
        mq += 'not ' + feature;
      } else {
        mq += '(' + feature + ': ' + value + ')';
      }
      if (index < features.length-1) {
        mq += ' and '
      }
    });
    return mq;
  }

  function json2mq(query) {
    var mq = '';
    if (typeof query === 'string') {
      return query;
    }
    // Handling array of media queries
    if (query instanceof Array) {
      query.forEach(function (q, index) {
        mq += obj2mq(q);
        if (index < query.length-1) {
          mq += ', '
        }
      });
      return mq;
    }
    // Handling single media query
    return obj2mq(query);
  };

  function parseFont(fontString) {
    if (!fontString || !fontString.length) return null;

    const parts = fontString.split(':');
    const faceParts = parts[0].split(',');

    return {
      face: faceParts[0],
      generic: faceParts[1],
      weight: parts[1],
      size: parts[2],
    };
  }

  function fontToStyle(font) {
    let fontFamily = `'${font.face}'`;
    let fontSize;

    if (font.generic && font.generic.length) fontFamily = `${fontFamily},${font.generic}`;
    if (font.size) fontSize = `${font.size}px`

    return {
      fontFamily,
      fontWeight: font.weight,
      fontSize,
    };
  }

  function fontToString(font) {
    const f = _.extend({ face: 'Open Sans', generic: 'sans-serif', weight: '400', size: '16' }, font);

    return `${f.face},${f.generic}:${f.weight}:${f.size}`;
  }

  // Get full size media url
  function getMediaUrl(api, mediaId) {
    return `${MEDIA_URL}/${mediaId}`;
  }

  // Get thumbnail media url
  function getMediaThumbnailUrl(api, mediaId, width, height, format) {
    if (!api || !mediaId) return '';
    const { sumoUrl, thumbsUrl } = api.properties;
    const w = width || 60;
    const h = height || 60;
    const f = format || 'png';
    const mediaFile = `${mediaId}_${w}_${h}.${f}`;

    // If we are in dev, we want to go through our local server instead of the cdn.
    if (sumoUrl.indexOf('dev.sumome.com') >= 0) {
      return `${sumoUrl}/media/thumbs/v3/${mediaFile}`;
    }

    return `${thumbsUrl}/media/thumbs/v3/${mediaFile}`;
  }

  /**
   * Builds the webshot params for the given form type
   */
  function buildWebshotParams(formType) {
    const params = {
      w: 130,
      ww: 640,
      wh: 640,
    };

    if (formType === 'smart-bar') {
      Object.assign(params, {
        ww: 850,
        wh: 600,
        cx: 100,
        cw: 700,
        ch: 600,
      });
    } else if (formType === 'welcome-mat') {
      Object.assign(params, {
        ww: 1000,
        wh: 500,
      });
    }

    return params;
  }

  // Get template thumbnail media url
  function getTemplateThumbnailUrl(api, templateId, formType, encryptJSON) {
    const { microSumoUrl } = api.properties;
    const url = new URL(`${WEBSHOT_API_URL}/api/snap`);
    const dimensions = buildWebshotParams(formType);
    const searchParams = new URLSearchParams({
      url:
      encodeURIComponent(`https://sumome.com/apps/listbuilder/v3/template/${templateId}/phantom`),
      hashKey: encryptJSON,
      ...dimensions,
    });

    url.search = searchParams.toString();

    return url.toString();
  }

  function navigateTo(url, ref) {
    let fullUrl = url;

    if (ref) {
      fullUrl = `${url}#ref-${ref}`;
    }

    window.location.hash = '';
    setTimeout(function() {
      window.location.hash = fullUrl;
    }, 0);
  }

  function navigateToProduct(productId, ref) {
    navigateTo(`#/app_store/${productId}`, ref);
  }

  const navigateToPricing = () => {
    if (BILLING_URL && window) {
      if (window.location) {
        window.location.href = BILLING_URL;
      }
      else {
        window.location = BILLING_URL;
      }
      return;
    }
  };

  function addEvent(el, event, handler) {
    if (!el) { return; }
    if (el.attachEvent) {
      el.attachEvent('on' + event, handler);
    } else if (el.addEventListener) {
      el.addEventListener(event, handler, true);
    } else {
      // $FlowIgnore: Doesn't think elements are indexable
      el['on' + event] = handler;
    }
  }

  function removeEvent(el, event, handler) {
    if (!el) { return; }
    if (el.detachEvent) {
      el.detachEvent('on' + event, handler);
    } else if (el.removeEventListener) {
      el.removeEventListener(event, handler, true);
    } else {
      //$FlowIgnore: Doesn't think elements are indexable
      el['on' + event] = null;
    }
  }

  function getUUID(api, cb) {
    axios.post(`${api.properties.sumoUrl}/util/uuid`).then(res => {
      cb(res.data);
    }).catch(error => {
      console.log(error);
      cb(null);
    });
  }

  function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  function truncate(string, length) {
    if (!string) return '';
    return string.length <= length ? string : (string.substr(0, length) + '...');
  }

  function formatDate(date) {
    return moment(date).calendar(null, {
      lastDay: 'YYYY-MM-DD',
      sameDay: 'LT',
      nextDay: 'YYYY-MM-DD',
      lastWeek: 'YYYY-MM-DD',
      nextWeek: 'YYYY-MM-DD',
      sameElse: 'YYYY-MM-DD',
    });
  }

  function appHistory(api, ) {
    let history = ReactRouter.useRouterHistory(History.createMemoryHistory)({
      queryKey: false,
    });

    if (api.properties.mode === 'manage') {
      history = ReactRouter.hashHistory;
    }

    function changeListener(newLocation) {
      if (api.properties.mode === 'manage') return;

      if (window.history) {
        const basename = `/`;
        let path = window.location.pathname + window.location.search + window.location.hash;
        const uri = new URI(path);
        var normalizedPathname = newLocation.pathname.charAt(0) === '/' ? newLocation.pathname.slice(1) : newLocation.pathname;

        uri.search({ sumopath: basename + normalizedPathname });

        if (window.location.hash.indexOf('#/') === 0) window.location.hash = '';

        path = window.location.pathname + '?' + uri.query().replace(/%2F/g, '/') + window.location.hash;
        window.history.replaceState(window.history.state, '', path);
      }
    }

    function startRouteListener(history) {
      api.on(`/app/route/query`, (path) => {
        history.push(path);
      });

      history.listen(changeListener);
    }

    function stopRouteListener() {
      api.off(`/app/route`);
    }

    let listenerCount = 0;

    function listen(listener) {
      if (++listenerCount === 1) startRouteListener(history);

      const unlisten = history.listen(listener);

      return () => {
        unlisten();

        if (--listenerCount === 0) stopRouteListener();
      };
    }

    return _.extend({}, history, {
      listen: listen,
    });
  }

  function pluralize(word, count){
    if (count != 1) word += 's';
    return word;
  }

  /**
   * Calculate a 32 bit FNV-1a hash
   * Found here: https://gist.github.com/vaiorabbit/5657561
   * Ref.: http://isthe.com/chongo/tech/comp/fnv/
   *
   * @param {string} str the input value
   * @param {integer} [seed] optionally pass the hash of the previous chunk
   * @returns {string}
   */
  function hashFnv32a(str, seed) {
    if (str) {
      /* eslint no-bitwise: 0 */
      let i;
      let l;
      let hval = seed || 0x811c9dc5;

      for (i = 0, l = str.length; i < l; i += 1) {
        hval ^= str.charCodeAt(i);
        hval += (hval << 1) + (hval << 4) + (hval << 7) + (hval << 8) + (hval << 24);
      }
      // Convert to 8 digit hex string
      return (`0000000${(hval >>> 0).toString(16)}`).substr(-8);
    }
    return '00000000';
  }

  const rgbaToHex = (color, transparentToNull) => {
    if (color.indexOf('#') === 0) return color; // is already hex, return

    const colorValues = color
      .substr(color.indexOf('(') + 1)
      .split(',')
      .map(num => parseInt(num, 10).toString(16).toUpperCase());

    if (transparentToNull && colorValues.length === 4 && colorValues[3] === '0') {
      return null;
    }

    return `#${
      colorValues
        .map(hex => (hex.length === 1 ? `0${hex}` : hex))
        .slice(0, 3)
        .join('')
    }`;
  };

  const hexToRGB = (color) => {
    if (color.length === 4) {
      // Shorthand
      const r = color.charAt(1);
      const g = color.charAt(2);
      const b = color.charAt(3);
      return `rgb(${parseInt(r + r, 16)},${parseInt(g + g, 16)},${parseInt(b + b, 16)})`;
    }

    const r = color.substr(1, 2);
    const g = color.substr(3, 2);
    const b = color.substr(5, 2);
    return `rgb(${parseInt(r, 16)},${parseInt(g, 16)},${parseInt(b, 16)})`;
  };

  function zeroPrefix(unit){
    return ('0' + unit).slice(-2);
  }

  function numberWithCommas(x) {
    const parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  function totalHours(countdownTime){
    let countdownTimeInHours = Math.floor(countdownTime.value / (1000*60*60));
    if (countdownTimeInHours < 10) countdownTimeInHours = zeroPrefix(countdownTimeInHours);
    return numberWithCommas(countdownTimeInHours);
  }

  function formatUnit(unit, noun, format){
    return format.replace('UU', unit)
                 .replace('uu', unit)
                 .replace('NN', pluralize(noun, unit))
                 .replace('nn', noun.substring(0, 1).toLowerCase())
                 .replace('Nn', noun.substring(0, 1).toUpperCase());
  }

  //  UU NN,            4 days, 22 hours, 58 minutes, and 43 seconds
  //  UU NN, hh:mm:ss   4 days, 22:58:43
  //  UU nn, hh:mm:ss   4d, 22:58:43
  //  HH:mm:ss          118:58:43
  function formatTime(countdownTime, format) {
    const lowercaseFormat = format.toLowerCase();
    let output = format;

    if (countdownTime.value <= 0) {
      // zeros out time past
      for (let k in countdownTime) countdownTime[k] = 0;
    }

    output = output.replace('yy', zeroPrefix(countdownTime.years));
    output = output.replace('MM', zeroPrefix(countdownTime.months));
    output = output.replace('dd', zeroPrefix(countdownTime.days));
    output = output.replace('HH', totalHours(countdownTime));
    output = output.replace('hh', zeroPrefix(countdownTime.hours));
    output = output.replace('mm', zeroPrefix(countdownTime.minutes));
    output = output.replace('ss', zeroPrefix(countdownTime.seconds));

    // If has units (uu) and noun (nn) exists, and total hours doesn't,
    // we collect uu and nn and any chars after that are part of the units/noun
    // combo.  So each unit (seconds, minutes, hours, etc.) will have that suffix.
    // For the last unit, we remove the suffix part.
    const unitIdx = lowercaseFormat.indexOf('uu');
    const nounIdx = lowercaseFormat.indexOf('nn');
    if (
      unitIdx > -1 &&
      nounIdx > -1 &&
      format.indexOf('HH') === -1
    ) {
      const start = unitIdx < nounIdx ? unitIdx : nounIdx;
      const end = unitIdx > nounIdx ? unitIdx + 2 : nounIdx + 2;
      let unitFormat = format.substring(start, end);
      let joinStr = '';

      // Collec the suffix on each unit.
      for (let i = 0; end + i < format.length; i++) {
        const currentIndex = end + i;
        const thisChar = format.substring(currentIndex, currentIndex + 1);
        const nextChar = format.substring(currentIndex + 1, currentIndex + 2);
        if (UNITS[thisChar + nextChar]) break;

        joinStr += thisChar;
      }

      unitFormat += joinStr;

      // For all units that aren't in the format and exist, we want
      // to add them to the unit (uu) section with the format given.
      let unitsOutput = '';
      if (format.indexOf('ss') === -1) {
        const article = countdownTime.value > (1000 * 59) ? 'and ' : '';
        const formattedUnits = formatUnit(countdownTime.seconds, UNITS['ss'], unitFormat);
        unitsOutput = article + formattedUnits + unitsOutput;
      }
      if (countdownTime.minutes && format.indexOf('mm') === -1) {
        const formattedUnits = formatUnit(countdownTime.minutes, UNITS['mm'], unitFormat);
        unitsOutput = formattedUnits + unitsOutput;
      }
      if (countdownTime.hours && format.indexOf('hh') === -1) {
        const formattedUnits = formatUnit(countdownTime.hours, UNITS['hh'], unitFormat);
        unitsOutput = formattedUnits + unitsOutput;
      }
      if (countdownTime.days && format.indexOf('dd') === -1) {
        const formattedUnits = formatUnit(countdownTime.days, UNITS['dd'], unitFormat);
        unitsOutput = formattedUnits + unitsOutput;
      }
      if (countdownTime.months && format.indexOf('MM') === -1) {
        const formattedUnits = formatUnit(countdownTime.months, UNITS['MM'], unitFormat);
        unitsOutput = formattedUnits + unitsOutput;
      }
      if (countdownTime.years && format.indexOf('yy') === -1) {
        const formattedUnits = formatUnit(countdownTime.years, UNITS['yy'], unitFormat);
        unitsOutput = formattedUnits + unitsOutput;
      }

      // If there aren't any extras on the end of the format (checked by absence of
      // ss, mm, and hh) we don't need the last unit's suffix.
      if(format.indexOf('ss') === -1 && format.indexOf('mm') === -1 && format.indexOf('hh') === -1) {
        unitsOutput = unitsOutput.slice(0, (joinStr.length*(-1)));
      }

      // Finally, substitute our units string for the format in the output.
      output = output.replace(unitFormat, unitsOutput);
    }

    return output;
  }

  function isValidURL(str) {
    const pattern = new RegExp(/^https?:\/\/.+/i);
    return !!pattern.test(str);
  }

  const scrollTo = (el, to, duration, done = Function.prototype) => {
    to = Math.min(to, el.scrollHeight - el.offsetHeight);
    let { scrollTop } = el;
    if (scrollTop === to) {
      done();
      return null;
    }

    const diff = to - scrollTop;
    const scrollStep = (Math.PI * 10) / duration;
    const start = scrollTop;

    const totalSteps = duration / 10;
    let count = 0;
    let timeout;
    let scrollInterval = setInterval(() => {
      ({ scrollTop } = el);
      if (Math.abs(scrollTop - to) > 1 && count <= totalSteps) {
        count += 1;
        const curPos = start + diff * (Math.round((0.5 - 0.5 * Math.cos(count * scrollStep)) * 100) / 100);
        el.scrollTo(0, curPos);
      } else {
        clearInterval(scrollInterval);
        if (timeout) clearTimeout(timeout);
        scrollInterval = null;
        done();
      }
    }, 10);

    return scrollInterval;
  };

  const validateEmail = email => /^[^@+]+(\+[^@+]+)?@[^@]+\.[a-zA-Z]{2,}$/.test(email || '');

  const formatMoney = (amount, symbol, isPrefix = true) => {
    if (isPrefix) return `${symbol}${amount}`;
    return `${amount}${symbol}`;
  };

  const camelCase = (string) => string.replace(/-([a-z])/g, g => g[1].toUpperCase());

  const cssStringToObject = (string) => {
    const regex = /([\w-]*)\s*:\s*([^;]*)/g;
    var match, styleObject = {};

    // Parse into object and convert dashed keys to camel case
    while(match = regex.exec(string)) {
      styleObject[camelCase(match[1])] = match[2].trim();
    }

    return styleObject;
  };

  /**
   * Array of possible image formats to test
   */
  const possibleImageFormats = [
    {
      uri: 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=',
      name: 'webp',
    },
    {
      uri: 'data:image/webp;base64,UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAABBxAR/Q9ERP8DAABWUDggGAAAADABAJ0BKgEAAQADADQlpAADcAD++/1QAA==',
      name: 'webp.alpha',
    },
    {
      uri: 'data:image/webp;base64,UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA',
      name: 'webp.animation',
    },
    {
      uri: 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=',
      name: 'webp.lossless',
    },
  ];

  /**
   * Determines if a given image format is supported by the browser
   * @return promise
   */
  const isImageFormatSupported = possibleImageFormat => new Promise((resolve) => {
    const { name, uri } = possibleImageFormat;
    const image = document.createElement('img');
    const handleImageEvent = e => resolve({
      name,
      isSupported: (e && e.type === 'load' && image.width === 1),
    });

    image.onload = handleImageEvent;
    image.onerror = handleImageEvent;

    image.src = uri;
  });

  /**
   * Returns a promise that resolves a list of images supported by the browser
   */
  const fetchSupportedImageFormats = () => (
    Promise.all(possibleImageFormats.map(isImageFormatSupported)).then(
      results => results.filter(result => result.isSupported).map(result => result.name),
    )
  );

  const cacheGet = (key) => {
    let value;

    try {
      value = JSON.parse(localStorage.getItem(key));
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('failed to get cache value for key', key, e); // eslint-disable-line no-console
      }
    }

    return value;
  };

  const cacheSet = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));

      return true;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('failed to set cache value for key', key, e); // eslint-disable-line no-console
      }
    }

    return false;
  };

  const cacheGetSiteNs = () => {
    const matches = window.location.href.match(/site\/([0-9a-z]*)?\//) || [''];

    if (matches) {
      return matches.pop().substr(0, 8);
    }

    return '';
  }

  const capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  return {
    addEvent,
    appHistory,
    camelCase,
    classNames,
    Constants,
    containsPrize,
    copyToClipboard,
    createReducer,
    cssStringToObject,
    fontToString,
    fontToStyle,
    formatDate,
    formatMoney,
    formatTime,
    generateId64,
    getMediaThumbnailUrl,
    getMediaUrl,
    getQueryVariable,
    getQueryVariables,
    getTemplateThumbnailUrl,
    getUUID,
    hashFnv32a,
    hexToRGB,
    isMobile,
    isValidURL,
    linkState,
    navigateTo,
    navigateToPricing,
    navigateToProduct,
    numberWithCommas,
    parseFont,
    removeEvent,
    rgbaToHex,
    scrollTo,
    serverCalls,
    sumoApi,
    truncate,
    validateEmail,
    fetchSupportedImageFormats,
    cacheGet,
    cacheSet,
    cacheGetSiteNs,
    capitalize,
  };
});
